import axios, { AxiosResponse } from "axios";
import {
  MerchantInterface,
  MerchantPingInterface,
  ResponseInterface,
} from "../interfaces";
import { Nullable } from "../interfaces/GenericTypes";
import { merchantFormat } from "../utils";
import { alertService } from "./alertService";

const testURL = "http://localhost:8085/vpos/ping";
const baseURL = "http://localhost:8085/vpos/metodo";
const printerURL = process.env.REACT_APP_API_PRINTER_HOST;
const defaultPrinter = "xp-80"; 

// The vpos may accept the connection and never answer; axios has no default
// timeout, so bound the version check
const PING_TIMEOUT_MS = 3000;

// Merchant misc options
enum MiscelaneousMerchantMethods {
  "imprimeUltimoVoucher",
  "imprimeUltimoVoucherP",
  "precierre",
  "cierre",
  "ultimoCierre",
}

// Vouchers available through the local printer service (GET /voucher/{command})
export type MerchantVoucherCommand =
  | "aprobado"
  | "procesado"
  | "precierre"
  | "cierre";

// Request header
const header = {
  headers: {
    "Content-Type": "application/json",
  },
};

// The vpos answers the ping as text/plain: one "Label: value" pair per line,
// and lines that hold several pairs separate them with commas. Values are read
// up to the next ", Label:" so the colons inside a value (the activity time)
// do not break the pair
const PING_PAIR_PATTERN =
  /([A-Za-z][A-Za-z0-9 ]*?)\s*:\s*(.*?)(?=,\s*[A-Za-z][A-Za-z0-9 ]*\s*:|$)/g;

// "Version VPos" -> "versionVPos", "MaxMemory Heap" -> "maxMemoryHeap". Only
// the first letter of each word is touched, so the inner capitals the vpos
// writes survive
const toPingKey = (label: string): string =>
  label
    .trim()
    .split(/\s+/)
    .map((word, index) =>
      index === 0
        ? word.charAt(0).toLowerCase() + word.slice(1)
        : word.charAt(0).toUpperCase() + word.slice(1)
    )
    .join("");

const parsePingValue = (raw: string): unknown => {
  const value = raw.trim();

  if (value === "" || value.toLowerCase() === "null") return null;

  // A bracketed value carries several tokens, such as the date and time of
  // "Aplicacion activa : [01/09/2026 02:02:02]"
  const bracketed = /^\[(.*)\]$/.exec(value);

  if (bracketed) {
    return bracketed[1].split(/[\s,]+/).filter((token) => token !== "");
  }

  return value;
};

// Turns the ping answer into an object. The body is parsed as text unless the
// vpos already replied with JSON, so a build that answers either way is read
// the same
export const parseMerchantPing = (data: unknown): MerchantPingInterface => {
  if (data && typeof data === "object") return data as MerchantPingInterface;
  if (typeof data !== "string") return {};

  const text = data.trim();

  if (text.charAt(0) === "{") {
    try {
      return JSON.parse(text) as MerchantPingInterface;
    } catch (error) {
      // Not JSON after all; fall through and read it as text
    }
  }

  const ping: MerchantPingInterface = {};

  text.split(/\r?\n/).forEach((line) => {
    // A fresh regex per line keeps the shared lastIndex out of the loop
    const pairs = new RegExp(PING_PAIR_PATTERN.source, "g");
    let match = pairs.exec(line);

    while (match !== null) {
      const key = toPingKey(match[1]);

      if (key) ping[key] = parsePingValue(match[2]);

      match = pairs.exec(line);
    }
  });

  return ping;
};

// Ping merchant service
export const pingMerchant = async (): Promise<
  Nullable<MerchantPingInterface>
> => {
  try {
    const response = await axios.get(testURL, {
      timeout: PING_TIMEOUT_MS,
      // The answer is text/plain: take the body untouched and shape it here
      responseType: "text",
    });

    if (response.status === 200) {
      return parseMerchantPing(response.data);
    }
  } catch (error) {
    console.error("Error pinging merchant:", error);
  }

  return null;
};

// Local printing is only available from this vpos version onwards. Older
// versions print every voucher directly from the merchant interface, so the
// app must not print locally nor offer print buttons
const MIN_LOCAL_PRINTING_VERSION = { major: 3, minor: 16 };

// Whether merchant vouchers are read and printed through the local printer
// service. Only a vpos that reports 3.16 or above turns it on: while the
// version is unknown the app behaves as it did before local printing existed,
// which is what every older vpos expects
let localPrintingEnabled = false;

// Extracts the leading "major.minor" of the VersionVPos value, ignoring the
// build prefix and any extra segment (e.g. "JARE.3.16.0" -> 3.16). Reading the
// trailing pair instead would take "3.15.0.4" for 15.0 and "4.0.0.0" for 0.0
const parseVposVersion = (
  version: string
): Nullable<{ major: number; minor: number }> => {
  const cleaned = version.trim().replace(/^[A-Za-z]+[\s._-]*/, "");
  const match = /^(\d+)\.(\d+)(?:[\s._-]|$)/.exec(cleaned);

  if (!match) return null;

  return {
    major: Number.parseInt(match[1], 10),
    minor: Number.parseInt(match[2], 10),
  };
};

// Reads the VersionVPos field of the ping response without depending on the
// exact key casing of the vpos build
const getVposVersion = (ping: MerchantPingInterface): Nullable<string> => {
  const key = Object.keys(ping).find(
    (k) => k.replace(/[^a-z]/gi, "").toLowerCase() === "versionvpos"
  );
  const value = key ? ping[key] : null;

  return typeof value === "string" ? value : null;
};

// In-flight or already settled version check. A conclusive answer is kept for
// the rest of the session - the vpos is not upgraded while the app runs - so
// the sections that mount share a single ping
let localPrintingCheck: Nullable<Promise<boolean>> = null;

// Pings the vpos and resolves whether vouchers are read and printed through
// the local printer service. Versions below 3.16 handle all of it from the
// merchant interface itself. A ping that answers nothing, or reports no
// readable version, leaves local printing off and re-arms the check, so a vpos
// that was still starting up is picked up by the next section that mounts
const runLocalPrintingCheck = async (): Promise<boolean> => {
  const ping = await pingMerchant();
  const version = ping ? getVposVersion(ping) : null;
  const parsed = version ? parseVposVersion(version) : null;

  if (parsed) {
    localPrintingEnabled =
      parsed.major > MIN_LOCAL_PRINTING_VERSION.major ||
      (parsed.major === MIN_LOCAL_PRINTING_VERSION.major &&
        parsed.minor >= MIN_LOCAL_PRINTING_VERSION.minor);
  } else {
    localPrintingCheck = null;
  }

  return localPrintingEnabled;
};

export const checkMerchantLocalPrinting = (): Promise<boolean> => {
  if (!localPrintingCheck) localPrintingCheck = runLocalPrintingCheck();

  return localPrintingCheck;
};

// Guards the entry points reached from sections that never refresh the flag
// themselves, such as the merchant test lab
const ensureLocalPrintingChecked = async (): Promise<void> => {
  await checkMerchantLocalPrinting();
};

// Answer of every local printer service call while local printing is
// disabled, so no request ever leaves the app
const localPrintingDisabledResponse = (): ResponseInterface<string> => ({
  message: "",
  didError: true,
  errorMessage:
    "La impresión local está deshabilitada: esta versión del vpos imprime los vouchers desde la interfaz Merchant.",
  model: null,
});

// Print the voucher generated by a merchant operation through the local
// printer service, without holding up the flow that triggered it
const printMerchantResponseVoucher = async (
  merchant: Nullable<MerchantInterface>
) => {
  if (!merchant?.nombreVoucher) return;

  await ensureLocalPrintingChecked();

  // Below 3.16 the vpos already printed the voucher by itself
  if (!localPrintingEnabled) return;

  printMerchantVoucherFile(merchant.nombreVoucher).then((printed) => {
    if (printed.didError) {
      alertService.error(
        "No se pudo imprimir el voucher",
        printed.errorMessage
      );
    }
  });
};

// Pay through merchant service
export const payMerchant = async (
  amount: number,
  ci: string,
  zelleDescription?: string
): Promise<Nullable<MerchantInterface>> => {
  const amountString = merchantFormat(amount);

  const body = {
    accion: "tarjeta",
    montoTransaccion: amountString,
    cedula: ci,
    referencia: zelleDescription,
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      const merchant: Nullable<MerchantInterface> = response.data;

      printMerchantResponseVoucher(merchant);

      return merchant;
    }
  } catch (error) {}

  return null;
};

export const annulationMerchant = async (
  seq: string
): Promise<Nullable<MerchantInterface>> => {
  const body = {
    accion: "anulacion",
    numSeq: seq,
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      const merchant: Nullable<MerchantInterface> = response.data;

      printMerchantResponseVoucher(merchant);

      return merchant;
    }
  } catch (error) {}

  return null;
};

// Pay change with merchant service
export const changeMerchant = async (
  amount: number,
  ci: string
): Promise<Nullable<MerchantInterface>> => {
  const amountString = merchantFormat(amount);
  const body = {
    accion: "cambio",
    montoTransaccion: amountString,
    cedula: ci,
    tipoMoneda: "VES",
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      const merchant: Nullable<MerchantInterface> = response.data;

      printMerchantResponseVoucher(merchant);

      return merchant;
    }
  } catch (error) {}

  return null;
};

// Options of the misc merchant methods
interface MiscMerchantOptions {
  // Whether the voucher the operation generates is sent to the printer. The
  // visualization modal turns it off: it shows the voucher first and prints
  // it from its own button
  print?: boolean;
}

// Use other merchant services
export const miscMerchant = async (
  command: number,
  { print = true }: MiscMerchantOptions = {}
): Promise<Nullable<MerchantInterface>> => {
  const body = {
    accion: MiscelaneousMerchantMethods[command],
  };
  try {
    const response = await axios.post(baseURL, body, header);

    if (response.status === 200) {
      const merchant: Nullable<MerchantInterface> = response.data;

      // The precierre voucher only prints when the vpos approves the
      // operation (codRespuesta "00"). The cierre voucher never prints
      // here: it is shown in a modal where the user can print it
      const skipPrint =
        !print ||
        (command === MiscelaneousMerchantMethods.precierre &&
          merchant?.codRespuesta !== "00");

      if (!skipPrint && command !== MiscelaneousMerchantMethods.cierre) {
        printMerchantResponseVoucher(merchant);
      }

      return merchant;
    }
  } catch (error) {}

  return null;
};

// Misc merchant method that generates each voucher shown in the
// visualization modal. "cierre" maps to ultimoCierre because cierre (3)
// executes a new close
const voucherMiscMethods: Record<MerchantVoucherCommand, number> = {
  aprobado: MiscelaneousMerchantMethods.imprimeUltimoVoucher,
  procesado: MiscelaneousMerchantMethods.imprimeUltimoVoucherP,
  precierre: MiscelaneousMerchantMethods.precierre,
  cierre: MiscelaneousMerchantMethods.ultimoCierre,
};

// Asks the vpos for one of the vouchers shown in the visualization modal and
// answers with the path of the file it generated (nombreVoucher). Nothing is
// printed here: the modal reads that file and prints it on demand
export const getMerchantVoucherPath = async (
  command: MerchantVoucherCommand
): Promise<ResponseInterface<string>> => {
  await ensureLocalPrintingChecked();

  // Below 3.16 the vpos shows and prints its vouchers by itself
  if (!localPrintingEnabled) return localPrintingDisabledResponse();

  const merchant = await miscMerchant(voucherMiscMethods[command], {
    print: false,
  });

  // The file is shown whenever the vpos wrote one, even if the transaction it
  // belongs to was declined: that ticket is precisely what was asked for
  if (merchant?.nombreVoucher) {
    return {
      message: "",
      didError: false,
      errorMessage: "",
      model: merchant.nombreVoucher,
    };
  }

  return {
    message: "",
    didError: true,
    errorMessage:
      merchant?.mensajeRespuesta ||
      "No hubo respuesta de Merchant, verifique que la interfaz esté activa.",
    model: null,
  };
};

// Perform a request to the local printer service, normalizing failures to
// the ResponseInterface shape
const requestVoucherService = async (
  request: () => Promise<AxiosResponse<ResponseInterface<string>>>,
  failMessage: string
): Promise<ResponseInterface<string>> => {
  try {
    const response = await request();

    if (response.status === 200) {
      return response.data;
    }

    return {
      message: "",
      didError: true,
      errorMessage: failMessage,
      model: null,
    };
  } catch (error) {
    // The service answers with BadRequest and the same shape when it fails
    const data = (error as any)?.response?.data as
      | ResponseInterface<string>
      | undefined;

    return {
      message: data?.message ?? "",
      didError: true,
      errorMessage:
        data?.errorMessage ??
        "No se pudo comunicar con el servicio de impresión, verifique que el programa se esté ejecutando.",
      model: null,
    };
  }
};

// Get the text of a merchant voucher from the local printer service
export const getMerchantVoucher = async (
  command: MerchantVoucherCommand
): Promise<ResponseInterface<string>> => {
  await ensureLocalPrintingChecked();

  // Below 3.16 the vpos shows and prints its vouchers by itself
  if (!localPrintingEnabled) return localPrintingDisabledResponse();

  return await requestVoucherService(
    () =>
      axios.post<ResponseInterface<string>>(
        `${printerURL}/voucher/${command}`,
        header
      ),
    `No se pudo obtener el voucher '${command}'.`
  );
};

// Get the text of a voucher file given its full path on the machine that
// runs the local printer service
export const getMerchantVoucherFile = async (
  filePath: string
): Promise<ResponseInterface<string>> => {
  await ensureLocalPrintingChecked();

  // Below 3.16 the vpos shows and prints its vouchers by itself
  if (!localPrintingEnabled) return localPrintingDisabledResponse();

  return await requestVoucherService(
    () =>
      axios.post<ResponseInterface<string>>(
        `${printerURL}/voucher/${encodeURIComponent(filePath)}`,
        header
      ),
    "No se pudo obtener el voucher del cierre."
  );
};

// Get the voucher text and notify the user when it could not be read
export const getMerchantVoucherText = async (
  command: MerchantVoucherCommand
): Promise<Nullable<string>> => {
  const response = await getMerchantVoucher(command);

  if (response.didError || !response.model) {
    alertService.error("No se pudo obtener el voucher", response.errorMessage);
    return null;
  }

  return response.model;
};

// Send a voucher to print through the local printer service
// (POST /voucher/{command}/print?printer={printer}). The command accepts the
// same values as the voucher visualization, or the full path of a voucher file
const requestMerchantVoucherPrint = async (
  command: string,
  printer: string = defaultPrinter
): Promise<ResponseInterface<string>> => {
  await ensureLocalPrintingChecked();

  // Below 3.16 the vpos prints its vouchers by itself
  if (!localPrintingEnabled) return localPrintingDisabledResponse();

  return await requestVoucherService(
    () =>
      axios.post<ResponseInterface<string>>(
        `${printerURL}/voucher/${encodeURIComponent(command)}/print`,
        null,
        { ...header, params: { printer } }
      ),
    `No se pudo imprimir el voucher '${command}'.`
  );
};

// Print one of the vouchers available through the local printer service,
// using the same commands as the voucher visualization
export const printMerchantVoucher = async (
  command: MerchantVoucherCommand,
  printer?: string
): Promise<ResponseInterface<string>> =>
  await requestMerchantVoucherPrint(command, printer);

// Print a voucher file given its full path on the machine that runs the
// local printer service
export const printMerchantVoucherFile = async (
  filePath: string,
  printer?: string
): Promise<ResponseInterface<string>> =>
  await requestMerchantVoucherPrint(filePath, printer);

// Voucher of the last approved transaction (vpos.txt)
export const getApprovedVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("aprobado");

// Voucher of the last processed transaction (ultTxnProc.txt)
export const getProcessedVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("procesado");

// Latest pre-closing report (precierres folder)
export const getPreClosingVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("precierre");

// Latest closing report (cierres folder)
export const getClosingVoucher = async (): Promise<Nullable<string>> =>
  await getMerchantVoucherText("cierre");
